import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';

// 🎯 Phase 2 Sprint 1 W1 day 2 (2026-06-26): lazy-load di xlsx-js-style
// via `dynamic import()` invece di top-level `import`. Pre-cleanup,
// il bundle iniziale includeva sempre `xlsx-js-style/dist` +
// `cpexcel.js` + `xlsx.min.js` (~700 KB gzipped totale) anche per
// utenti che non aprono mai la pagina statistiche → unica pagina
// che fa Excel export. Ora la lib viene caricata SOLO al primo
// click su "Export Excel" via webpack code-split.
//
// `import type` è TypeScript-only (strippato in compile) → fornisce
// i tipi senza forzare il bundle a includere la lib. I metodi
// pubblici diventano `async` ma i call site fire-and-forget non
// hanno regressioni visibili (il consumer in `statistiche.component`
// chiama il metodo senza `await` e l'utente vede il download Excel
// dopo qualche centinaio di ms in più al primo click — accettabile,
// click successivi sono istantanei grazie alla cache webpack runtime).
//
// Per chi rilegge in futuro: NON re-introdurre `import * as XLSX
// from 'xlsx-js-style'` top-level. Se serve una nuova capability
// Excel, aggiungi un metodo `async` qui che fa il suo
// `await import('xlsx-js-style')` interno.
type XLSXModule = typeof import('xlsx-js-style');

@Injectable({
  providedIn: 'root',
})
export class ExcelService {

  async exportAsExcelFile(
    json: any[],
    fileName: string,
    titleText?: string,
    firstColumnHeader: string = 'Mese',
  ): Promise<void> {
    const XLSX = await import('xlsx-js-style');

    const year = new Date().getFullYear();
    const title = titleText ?? `${fileName} - ${year}`;
    const headers = Object.keys(json[0]);
    const headerRow = Object.fromEntries(headers.map((h) => [h, h]));

    const newJson = [
      { [headers[0]]: title },
      {},
      {},
      headerRow,
      ...json,
    ];

    const ws = XLSX.utils.json_to_sheet(newJson, { skipHeader: true });

    this.applyStylesToWorksheet(XLSX, ws, json.length, firstColumnHeader);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, `${fileName}.xlsx`);
  }

  async exportAsExcelFileFromChart(
    labels: string[],
    datasets: { label: string; data: number[] }[],
    fileName: string,
    titleText?: string,
    firstColumnHeader: string = 'Mese',
  ): Promise<void> {
    const json: any[] = [];

    labels.forEach((label, i) => {
      const row: any = { [firstColumnHeader]: label };
      datasets.forEach((ds) => {
        row[ds.label] = ds.data[i] ?? 0;
      });
      json.push(row);
    });

    await this.exportAsExcelFile(json, fileName, titleText, firstColumnHeader);
  }

  /**
   * Riceve `XLSX` come parametro invece di importarlo top-level — questo
   * mantiene il bundle iniziale snello (vedi comment in cima al file).
   */
  private applyStylesToWorksheet(
    XLSX: XLSXModule,
    ws: any,
    dataLength: number,
    firstColumnHeader: string = 'Mese',
  ): void {
    const range = XLSX.utils.decode_range(ws['!ref']);

    const blackBorder = {
      top: { style: 'thin', color: { rgb: 'FF000000' } },
      bottom: { style: 'thin', color: { rgb: 'FF000000' } },
      left: { style: 'thin', color: { rgb: 'FF000000' } },
      right: { style: 'thin', color: { rgb: 'FF000000' } },
    };

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);
        if (!ws[cell_ref]) continue;

        const baseStyle: any = {
          border: blackBorder,
          alignment: { horizontal: 'center', vertical: 'center' },
        };

        if (R === 0) {
          baseStyle.font = { bold: true, sz: 18, color: { rgb: 'FF000000' } };
        }
        else if (R === 3) {
          baseStyle.font = { bold: true, sz: 14, color: { rgb: 'FFFFFFFF' } };
          if (C === 0) {
            baseStyle.fill = { fgColor: { rgb: 'FF1976D2' } };
          }
          else {
            baseStyle.fill = { fgColor: { rgb: 'FF388E3C' } };
          }
        }
        else if (R > 3) {
          if (C === 0) {
            baseStyle.font = { sz: 14, bold: true, color: { rgb: 'FF000000' } };
            baseStyle.fill = { fgColor: { rgb: 'FFD0E7FF' } };
            baseStyle.alignment.wrapText = true;
          }
          else {
            baseStyle.font = { sz: 12, color: { rgb: 'FF000000' } };
            const isEvenRow = (R - 3) % 2 === 0;
            if (isEvenRow) baseStyle.fill = { fgColor: { rgb: 'FFF0F0F0' } };
          }
        }

        ws[cell_ref].s = baseStyle;
      }
    }

    ws['!merges'] = ws['!merges'] || [];
    ws['!merges'].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: range.e.c },
    });

    ws['!rows'] = ws['!rows'] || [];
    for (let r = 4; r <= range.e.r; r++) {
      ws['!rows'][r] = { hpt: 25 };
    }
    ws['!rows'][0] = { hpt: 30 };
    ws['!rows'][3] = { hpt: 50 };

    const diagonalCell = ws['A4'];
    if (diagonalCell) {
      diagonalCell.v = firstColumnHeader;
      diagonalCell.t = 's';
      diagonalCell.s = diagonalCell.s || {};
      diagonalCell.s.border = {
        diagonal: { style: 'thin', color: { rgb: 'FF000000' } },
        diagonalUp: false,
        ...blackBorder,
      };
      diagonalCell.s.alignment = {
        horizontal: 'center',
        vertical: 'center',
        wrapText: true,
        textRotation: 0,
      };
      diagonalCell.s.fill = { fgColor: { rgb: 'FF0D47A1' } };
      diagonalCell.s.font = { bold: true, color: { rgb: 'FFFFFFFF' } };
    }

    const colWidths: { wch: number }[] = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxLen = 18;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
        const cell = ws[cellAddress];
        if (cell && cell.v) {
          const cellLen = cell.v.toString().length;
          if (cellLen > maxLen) maxLen = cellLen;
        }
      }
      colWidths.push({ wch: maxLen + 2 });
    }

    if (colWidths.length > 0) {
      colWidths[0] = { wch: 24 };
    }

    ws['!cols'] = colWidths;
  }

}
